import { Component, computed, effect, inject, input, signal, untracked, ViewEncapsulation } from '@angular/core';
import { take } from 'rxjs';
import {
	MD_DIALOG_REF,
	MDDialogModule,
	MDDialogRef,
	MDDropDownComponent,
	MDIconComponent,
	IAlert,
	MDAlertService,
	IMDDropDownOption
} from '@melodic-ui/components';
import { AttendanceService } from '@lb/api/mylb/services';
import { LoaderComponent } from '../loader/loader.component';
import { AccountStateService } from '@lb/state/account-state.service';

@Component({
	selector: 'lb-attendance-dialog',
	standalone: true,
	imports: [MDDialogModule, MDIconComponent, MDDropDownComponent, LoaderComponent],
	templateUrl: './attendance-dialog.component.html',
	styleUrl: './attendance-dialog.component.scss',
	encapsulation: ViewEncapsulation.None
})
export class AttendanceDialogComponent {
	private _dialogRef: MDDialogRef = inject(MD_DIALOG_REF);
	private _attendanceService = inject(AttendanceService);
	private _alertService = inject(MDAlertService);
	private _accountStateService = inject(AccountStateService);

	lessonID = input.required<number>();
	options = input.required<IMDDropDownOption[]>();
	currentAttendanceStatusID = input<number | null>();

	selectedStatusID = signal<number | null>(null);
	generateInvoice = signal<boolean>(true);
	isLoading = signal<boolean>(false);
	errorMessage = signal<string | null>(null);

	canConfirm = computed(() => this.selectedStatusID() != null && !this.isLoading());

	isAdmin = this._accountStateService.isAdmin;

	constructor() {
		effect(() => {
			const currentStatus = this.currentAttendanceStatusID();
			if (currentStatus != null) {
				untracked(() => this.selectedStatusID.set(currentStatus));
			}
		});
	}

	onStatusChange(value: unknown): void {
		this.selectedStatusID.set(value ? +value : null);
	}

	confirm(): void {
		const statusID = this.selectedStatusID();
		if (statusID == null || this.isLoading()) return;

		this.isLoading.set(true);
		this.errorMessage.set(null);

		// Include Invoice is an admin only option, non admins will always include the invoice log when updating attendance
		this._attendanceService
			.setAttendance(this.lessonID(), statusID, this.isAdmin() ? this.generateInvoice() : true)
			.pipe(take(1))
			.subscribe({
				next: () => {
					this.isLoading.set(false);
					this._alertService.addAlert({
						type: 'success',
						title: 'Attendance Updated',
						message: 'The lesson attendance has been successfully updated.',
						duration: 3000
					} as IAlert);
					this._dialogRef.close(true);
				},
				error: (error) => {
					const msg = error?.error?.message || 'Unable to update lesson attendance. Please try again.';
					this.errorMessage.set(msg);
					this.isLoading.set(false);
				}
			});
	}

	close(): void {
		this._dialogRef.close(false);
	}
}
